import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { z } from 'zod';

import { SearchActivityRuns } from '@robocorp/robocloud-search-interface';
import { activityTemplateService } from '~/services/activityTemplateAPI';
import { GitTaskPackageData } from '~/services/activityTemplateAPI/activityTemplateService';
import { getActivityRuns } from '~/services/searchAPI';
import { ErrorSubCodes } from '~/constants';
import { useBundleUploadMutation } from '../useBundleQuery';
import {
  ApiQueryFnData,
  formatErrorPayload,
  QueryError,
  UseApiInfiniteQueryOptions,
  UseApiMutationOptions,
  UseApiQueryOptions,
  validateApiResponse,
  withSearchSyncDelay,
} from '../utils';
import { useCurrentWorkspaceQueryKey } from '../utils/useQueryKey';

const ROOT_KEY = ['activity_templates'];
const getRootQueryKey = () => [ROOT_KEY];
export const getTaskPackageQueryKey = (id: string) => [...ROOT_KEY, id];
const getLinkQueryKey = (id: string) => [...ROOT_KEY, 'link', id];
const getStepRunQueryKey = (id: string, options?: Record<string, unknown>) => [
  ...ROOT_KEY,
  'step_runs',
  id,
  options,
];

export enum RobotsQueryErrors {
  LIST_ROBOTS_ERROR = 'LIST_ROBOTS_ERROR',
}

export const useActivityTemplatesQuery = (
  options?: UseApiQueryOptions<typeof activityTemplateService.getActivityTemplates>,
) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(getRootQueryKey()),
    async () =>
      validateApiResponse(await activityTemplateService.getActivityTemplates({ workspaceId })).data,
    {
      onError: (error) =>
        dispatch(formatErrorPayload(error, { type: RobotsQueryErrors.LIST_ROBOTS_ERROR })),
      ...options,
    },
  );
};

export const useActivityTemplateQuery = (
  id: string,
  options?: UseApiQueryOptions<typeof activityTemplateService.getActivityTemplateById>,
) => {
  const dispatch = useDispatch();
  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    queryKeyFormatter(getTaskPackageQueryKey(id)),
    async () =>
      validateApiResponse(
        await activityTemplateService.getActivityTemplateById({ workspaceId, id }),
      ).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useCreateActivityTemplateMutation = <
  T extends typeof activityTemplateService.createActivityTemplate,
>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ name }: { name: string }) =>
      validateApiResponse(
        await activityTemplateService.createActivityTemplate({ workspaceId, name }),
      ).data,
    {
      onSuccess: (item) => {
        if (item) {
          queryClient.setQueryData(queryKeyFormatter(getTaskPackageQueryKey(item.id)), item);
          queryClient.setQueryData(
            queryKeyFormatter(getRootQueryKey()),
            (items: unknown[] = []) => [...items, item],
          );

          withSearchSyncDelay(
            () => queryClient.invalidateQueries(queryKeyFormatter(getTaskPackageQueryKey(item.id))),
            {
              delayInMs: 5000,
            },
          );
        }
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useEditActivityTemplateMutation = <
  T extends typeof activityTemplateService.editActivityTemplate,
>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ activityTemplate }: { activityTemplate: ApiQueryFnData<T> }) =>
      validateApiResponse(
        await activityTemplateService.editActivityTemplate({
          workspaceId,
          template: activityTemplate,
        }),
      ).data,
    {
      onSuccess: (data, { activityTemplate }) => {
        queryClient.setQueryData(
          queryKeyFormatter(getTaskPackageQueryKey(activityTemplate.id)),
          data,
        );
        if (data) {
          queryClient.invalidateQueries(queryKeyFormatter(getRootQueryKey()));
        }
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const usePatchActivityTemplateMutation = <
  T extends typeof activityTemplateService.editActivityTemplate,
>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (activityTemplatePatch: Partial<ApiQueryFnData<T>> & { id: string }) => {
      const activityTemplate: ApiQueryFnData<T> | undefined = queryClient.getQueryData(
        queryKeyFormatter(getTaskPackageQueryKey(activityTemplatePatch.id)),
      );

      if (!activityTemplate) {
        throw new Error('failed to find task package to patch');
      }

      const updatedActivityTemplate = {
        ...activityTemplate,
        ...activityTemplatePatch,
        // TODO s3 files cannot be updated via ui
        // if config is not updated, use the old one
        config: activityTemplatePatch?.config || activityTemplate?.config,
      };

      return validateApiResponse(
        await activityTemplateService.editActivityTemplate({
          workspaceId,
          template: updatedActivityTemplate,
        }),
      ).data;
    },
    {
      onSuccess: (data, activityTemplate) => {
        queryClient.setQueryData(
          queryKeyFormatter(getTaskPackageQueryKey(activityTemplate.id)),
          data,
        );
        if (data) {
          queryClient.invalidateQueries(queryKeyFormatter(getRootQueryKey()));
        }
        withSearchSyncDelay(
          () =>
            queryClient.invalidateQueries(
              queryKeyFormatter(getTaskPackageQueryKey(activityTemplate.id)),
            ),
          { delayInMs: 5000 },
        );
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useDeleteActivityTemplateMutation = (
  options?: UseApiMutationOptions<typeof activityTemplateService.deleteActivityTemplateById>,
) => {
  const dispatch = useDispatch();
  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id }: { id: string }) =>
      validateApiResponse(
        await activityTemplateService.deleteActivityTemplateById({ workspaceId, id }),
      ).data,
    {
      onSuccess: (data) => {
        if (data) {
          queryClient.invalidateQueries(queryKeyFormatter(getRootQueryKey()));
        }
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useActivityTemplatesPackageLinkQuery = (
  id: string,
  options?: UseApiMutationOptions<typeof activityTemplateService.getPackageDownloadLink>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getLinkQueryKey(id),
    async () =>
      validateApiResponse(await activityTemplateService.getPackageDownloadLink({ workspaceId, id }))
        .data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useActivityTemplatesPackageUploadInfoMutation = (
  options?: UseApiMutationOptions<typeof activityTemplateService.getPackageUploadInfo>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ id }: { id: string }) =>
      validateApiResponse(await activityTemplateService.getPackageUploadInfo({ workspaceId, id }))
        .data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useActivityTemplatesGitRobotHandlerMutation = (
  options?: UseApiMutationOptions<typeof activityTemplateService.handleGitRobot>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ id, url }: { id: string; url: string }) =>
      validateApiResponse(await activityTemplateService.handleGitRobot({ workspaceId, id, url }))
        .data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useActivityTemplateUploadPackageMutation = (
  options?: UseApiMutationOptions<typeof activityTemplateService.getPackageUploadInfo>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { queryKeyFormatter } = useCurrentWorkspaceQueryKey();

  const { mutateAsync: activityTemplateUploadInfoAsync } =
    useActivityTemplatesPackageUploadInfoMutation();
  const { mutateAsync: bundleUploadAsync } = useBundleUploadMutation();

  return useMutation(
    async ({ id, file }: { id: string; file: File }) => {
      return activityTemplateUploadInfoAsync({ id }).then((info) => {
        const { fields } = info.postInfo;
        const { url } = info.postInfo;
        return bundleUploadAsync({ url, fields, file, config: { id } });
      });
    },
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(queryKeyFormatter(getTaskPackageQueryKey(id)));
        queryClient.invalidateQueries(queryKeyFormatter(getLinkQueryKey(id)));
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useActivityTemplateStepRunsInfiniteQuery = (
  {
    robotId,
    ...queryOptions
  }: Partial<z.infer<(typeof SearchActivityRuns)['request']['queryString']>> & {
    robotId: string;
    workspaceId: string;
  },
  options?: UseApiInfiniteQueryOptions<typeof getActivityRuns>,
) => {
  const dispatch = useDispatch();
  return useInfiniteQuery(
    getStepRunQueryKey(robotId, queryOptions),
    async ({ pageParam }) =>
      validateApiResponse(await getActivityRuns({ robotId, ...queryOptions, ...pageParam })).data,
    {
      getNextPageParam: (lastPage) => lastPage?.nextCursor,
      getPreviousPageParam: (firstPage) => firstPage?.previousCursor,
      onError: (error) => dispatch(formatErrorPayload(error)),
      keepPreviousData: true,
      ...options,
    },
  );
};

const getErrorTypeForErrorCode = (error: QueryError): ErrorSubCodes | undefined => {
  switch (error.code) {
    case 'task_package_with_name_already_exists':
      return ErrorSubCodes.ROBOT_WITH_SAME_NAME_ALREADY_EXISTS;
    case 'git_ref_not_found':
      return ErrorSubCodes.GIT_REFERENCE_NOT_FOUND_ERROR;
    case 'task_package_not_found':
      return ErrorSubCodes.TASK_PACKAGE_NOT_FOUND_ERROR;
    case 'task_package_used_by_step':
      return ErrorSubCodes.ERR_TEMPLATE_IN_USE;
    default:
      return undefined;
  }
};

export const useCreateTaskPackageMutation = (
  options?: UseApiMutationOptions<typeof activityTemplateService.createTaskPackage>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (data: GitTaskPackageData) =>
      validateApiResponse(await activityTemplateService.createTaskPackage({ workspaceId, ...data }))
        .data,
    {
      onSuccess: (item) => {
        if (item) {
          withSearchSyncDelay(
            () => queryClient.invalidateQueries(queryKeyFormatter(getTaskPackageQueryKey(item.id))),
            {
              delayInMs: 5000,
            },
          );
        }
      },
      onError: (error) => {
        dispatch(
          formatErrorPayload(error, {
            type: getErrorTypeForErrorCode(error),
          }),
        );
      },
      ...options,
    },
  );
};

export const useEditTaskPackageMutation = (
  options?: UseApiMutationOptions<typeof activityTemplateService.editTaskPackage>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ id, ...data }: GitTaskPackageData & { id: string }) =>
      validateApiResponse(await activityTemplateService.editTaskPackage({ workspaceId, id }, data))
        .data,
    {
      onSuccess: (_, taskPackage) => {
        queryClient.invalidateQueries(queryKeyFormatter(getTaskPackageQueryKey(taskPackage.id)));
        queryClient.invalidateQueries(queryKeyFormatter(getRootQueryKey()));
      },
      onError: (error) =>
        dispatch(formatErrorPayload(error, { type: getErrorTypeForErrorCode(error) })),
      ...options,
    },
  );
};

export const useDeleteTaskPackageMutation = (
  options?: UseApiMutationOptions<typeof activityTemplateService.deleteTaskPackage>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ id }: { id: string }) =>
      validateApiResponse(await activityTemplateService.deleteTaskPackage({ workspaceId, id }))
        .data,
    {
      onSuccess: (_, taskPackage) => {
        queryClient.invalidateQueries(queryKeyFormatter(getTaskPackageQueryKey(taskPackage.id)));
        queryClient.invalidateQueries(queryKeyFormatter(getRootQueryKey()));
      },
      onError: (error) =>
        dispatch(formatErrorPayload(error, { type: getErrorTypeForErrorCode(error) })),
      ...options,
    },
  );
};
